import React, { useEffect, useState } from "react";
import { api, formatNum, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import PeriodFilter from "@/components/PeriodFilter";
import { ChevronDown } from "lucide-react";
import { toast } from "sonner";

const STAGE_COLOR = [
  "from-[#0F2027] to-[#1F4B5A]",   // consultation
  "from-[#1F4B5A] to-[#0D9488]",
  "from-[#0D9488] to-[#10B981]",
  "from-[#10B981] to-[#22C55E]",
  "from-[#22C55E] to-[#84CC16]",
  "from-[#84CC16] to-[#EAB308]",
  "from-[#EAB308] to-[#F59E0B]",
  "from-[#F59E0B] to-[#F97316]",
];

export default function Funnel() {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState({});

  useEffect(() => {
    const params = new URLSearchParams();
    if (period.from) params.set("from_date", period.from);
    if (period.to) params.set("to_date", period.to);
    api.get(`/funnel/overview?${params}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [period]);

  if (!data) return <div data-testid="funnel-page" className="text-slate-500">Завантаження воронки...</div>;

  const maxCount = Math.max(...data.stages.map((s) => s.count), 1);

  return (
    <div data-testid="funnel-page" className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="heading text-3xl font-bold text-[#0F2027]">Воронка пацієнтів</h1>
          <p className="text-sm text-slate-500">Конверсія по етапах · всього: {formatNum(data.total_patients)} пацієнтів</p>
        </div>
        <PeriodFilter value={period} onChange={setPeriod} defaultPreset="all" />
      </div>

      <Card className="gv-card">
        <CardContent className="p-6 space-y-4">
          {data.stages.map((s, idx) => {
            const width = Math.max((s.count / maxCount) * 100, 8);
            return (
              <div key={s.stage} data-testid={`funnel-stage-${s.stage}`} className="space-y-1.5">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <span className="h-7 w-7 rounded-full bg-slate-100 text-slate-600 text-xs font-bold flex items-center justify-center">{idx + 1}</span>
                    <span className="font-medium text-[#0F2027]">{s.label}</span>
                    {s.conversion_from_prev !== null && (
                      <Badge variant="outline" className="text-[10px]">
                        <ChevronDown className="h-3 w-3 mr-0.5 text-[#0D9488]" />{s.conversion_from_prev}%
                      </Badge>
                    )}
                  </div>
                  <div className="font-bold tabular-nums text-[#0F2027]">{formatNum(s.count)}</div>
                </div>
                <div className="h-9 bg-slate-50 rounded-md overflow-hidden">
                  <div
                    className={`h-full bg-gradient-to-r ${STAGE_COLOR[idx % STAGE_COLOR.length]} transition-all duration-500 flex items-center px-3 text-white text-xs font-semibold`}
                    style={{ width: `${width}%` }}
                  >
                    {s.count > 0 && `${formatNum(s.count)} пацієнт.`}
                  </div>
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      <Card className="gv-card">
        <CardContent className="p-6">
          <h3 className="heading font-semibold text-[#0F2027] mb-3">Як управляти етапами</h3>
          <ol className="text-sm text-slate-600 space-y-1.5 list-decimal pl-5">
            <li>Зайдіть у модуль «Пацієнти» — поряд із кожним пацієнтом є селектор поточного етапу.</li>
            <li>Після завершення операції пацієнт автоматично переходить у статус «Прооперований».</li>
            <li>Етапи контролю (1 / 14 / 30 днів) проставляються вручну реєстратором або лікарем.</li>
            <li>У вікні історії пацієнта зберігається повний журнал переходів.</li>
          </ol>
        </CardContent>
      </Card>
    </div>
  );
}
